import { useCallback, useEffect, useMemo, useRef } from 'react'
import 'leaflet/dist/leaflet.css'
import type L from 'leaflet'
import type { ColorPair } from '@/features/subgeo/utils/color-utils'
import type { ViewOnlySubGeo } from '../types'
import {
	buildGeoJSONFeatureCollection,
	createGeoJSONLayer,
	createStyleFunction,
	filterValidSubgeos,
	fitMapToBounds,
	initializeLeafletMap
} from '../utils/map-utils'
import {
	cleanupTooltipRoots,
	createSubgeoTooltipElement,
	destroyAllTooltips
} from './subgeo-tooltip'

export interface GeometryCheckMapProps {
	subgeos: ViewOnlySubGeo[]
	getColor: (id: string) => ColorPair
	shouldZoomToFit?: boolean
}

export const GeometryCheckMap = ({
	subgeos,
	getColor,
	shouldZoomToFit
}: GeometryCheckMapProps) => {
	const mapRef = useRef<HTMLDivElement | null>(null)
	const mapInstance = useRef<L.Map | null>(null)
	const layerRef = useRef<L.GeoJSON | null>(null)
	const initializedRef = useRef(false)

	const validSubgeos = useMemo(() => filterValidSubgeos(subgeos), [subgeos])

	const featureCollection = useMemo(
		() => buildGeoJSONFeatureCollection(validSubgeos),
		[validSubgeos]
	)

	const styleFunction = useMemo(
		() => createStyleFunction(getColor),
		[getColor]
	)

	const createTooltip = useCallback(
		(name: string) => createSubgeoTooltipElement(name),
		[]
	)

	useEffect(() => {
		if (!mapRef.current || mapInstance.current) {
			return
		}

		mapInstance.current = initializeLeafletMap(mapRef.current)

		return () => {
			destroyAllTooltips()
			mapInstance.current?.remove()
			mapInstance.current = null
		}
	}, [])

	useEffect(() => {
		const map = mapInstance.current
		if (!(map && featureCollection)) {
			return
		}

		if (layerRef.current) {
			cleanupTooltipRoots()
			map.removeLayer(layerRef.current)
			layerRef.current = null
		}

		if (validSubgeos.length === 0) {
			return
		}

		const layer = createGeoJSONLayer(
			featureCollection,
			styleFunction,
			createTooltip
		)
		layer.addTo(map)
		layerRef.current = layer

		if (!initializedRef.current) {
			initializedRef.current = true
		}
	}, [featureCollection, validSubgeos.length, styleFunction, createTooltip])

	useEffect(() => {
		const map = mapInstance.current
		const layer = layerRef.current
		if (!(map && layer && shouldZoomToFit)) {
			return
		}

		fitMapToBounds(map, layer)
	}, [shouldZoomToFit])

	return <div className='h-full w-full' ref={mapRef} />
}
